"use client";

import * as React from "react";
import { useState } from "react";
import { cn } from "@repo/utils/cn";
import { useBodyScrollLock } from "@repo/utils/hooks/useBodyScrollLock";
import { Link } from "./Link";
import Image from "next/image";
import { AnimatePresence, motion } from "motion/react";
import { NavbarProps } from "./types";
import { SearchIcon } from "./icons/SearchIcon";
import { GlobeIcon } from "./icons/GlobeIcon";
import { ChevronDownIcon } from "./icons/ChevronDownIcon";
import { ChevronRightIcon } from "./icons/ChevronRightIcon";
import { MenuIcon } from "./icons/MenuIcon";
import { CloseIcon } from "./icons/CloseIcon";
import { Button } from "./Button";

export function NavbarMobile({
  logo,
  logoHref = "/",
  noFollow,
  items,
  language = "EN",
  onSearchClick,
  onLanguageClick,
  onSignInClick,
  onLogoClick,
  onHamburgerClick,
  onTopLevelLinkClick,
  onCategoryLinkClick,
  onSubCategoryLinkClick,
  signInText = "Sign in",
  signInHref,
  signInNoFollow,
  signInItems,
  activeTopLevelItemId,
  activeSubItemId,
  pathname,
  className,
}: NavbarProps) {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeSubgroup, setActiveSubgroup] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleItemClick = (item: any) => {
    if (item.hasDropdown && item.columns) {
      if (activeDropdown !== item.id) {
        onTopLevelLinkClick?.(item.title, item.href || "#");
      }
      setActiveDropdown(activeDropdown === item.id ? null : item.id);
      setActiveSubgroup(null);
    }
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (!isMobileMenuOpen) {
      onHamburgerClick?.();
    }
  };

  useBodyScrollLock(isMobileMenuOpen);

  return (
    <>
      <nav
        className={cn(
          "uipm:bg-[var(--surface-surface-primary)] uipm:border-b uipm:border-[var(--color-color-light)] uipm:fixed uipm:left-0 uipm:right-0 uipm:z-[60]",
          className,
        )}
        style={{ top: "var(--notification-banner-height, 0px)" }}
        data-navbar
      >
        <div className="uipm:flex uipm:items-center uipm:justify-between uipm:w-full uipm:h-[60px] uipm:px-4 uipm:py-2">
          {logo && (
            <Link
              href={logoHref}
              noFollow={noFollow}
              onClick={onLogoClick}
              aria-label={logo.alt || "Home"}
              className="uipm:shrink-0 uipm:cursor-pointer"
            >
              <Image
                src={logo.src}
                alt={logo.alt}
                width={logo.width}
                height={logo.height}
                className="uipm:h-[32px] uipm:w-auto"
              />
            </Link>
          )}

          <div className="uipm:flex uipm:items-center uipm:gap-4">
            {/*<button*/}
            {/*  onClick={onSearchClick}*/}
            {/*  className="uipm:w-6 uipm:h-6 hover:uipm:opacity-70 uipm:transition-opacity uipm:cursor-pointer"*/}
            {/*  aria-label="Search"*/}
            {/*>*/}
            {/*  <SearchIcon color="var(--typography-colors-text-accent)" />*/}
            {/*</button>*/}

            <button
              onClick={handleMobileMenuToggle}
              className="uipm:w-6 uipm:h-6 hover:uipm:opacity-70 uipm:transition-opacity uipm:cursor-pointer"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? (
                <CloseIcon color="var(--typography-colors-text-accent)" />
              ) : (
                <MenuIcon color="var(--typography-colors-text-accent)" />
              )}
            </button>
          </div>
        </div>
      </nav>
      {/* Spacer to prevent content from shifting under fixed navbar and banner */}
      <div
        className="uipm:lg:hidden"
        style={{
          height: "calc(var(--notification-banner-height, 0px) + 60px)",
        }}
        aria-hidden="true"
      />

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="uipm:fixed uipm:left-0 uipm:w-full uipm:bg-[var(--surface-surface-primary)] uipm:z-50"
            style={{
              top: "calc(var(--notification-banner-height, 0px) + 60px)",
              height:
                "calc(100vh - var(--notification-banner-height, 0px) - 60px)",
            }}
          >
            <div className="uipm:flex uipm:flex-col uipm:h-full uipm:overflow-hidden">
              <div
                className="uipm:flex-1 uipm:overflow-y-auto uipm:overflow-x-hidden uipm:p-[var(--margin-margin-sm)] uipm:pb-[120px]"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                <div>
                  {items.map((item) => {
                    const isActive = activeTopLevelItemId === item.id;
                    return (
                      <div key={item.id}>
                        {item.hasDropdown ? (
                          <>
                            <button
                              onClick={() => handleItemClick(item)}
                              className={cn(
                                "uipm:flex uipm:items-center uipm:justify-between uipm:w-full uipm:text-left uipm:text-(length:--typography-sizes-body-medium) uipm:font-bold uipm:leading-[var(--typography-line-heights-body-medium)] uipm:border-b uipm:border-[var(--color-color-light)] uipm:cursor-pointer uipm:h-[64px]",
                                isActive
                                  ? "uipm:text-[var(--color-color-accent)]"
                                  : "uipm:text-[var(--typography-colors-text-faded-dark)]",
                              )}
                            >
                              <span className="uipm:flex-1">{item.title}</span>
                              <ChevronDownIcon
                                className="uipm:transition-transform uipm:self-center"
                                color="var(--typography-colors-text-accent)"
                                style={{
                                  transform:
                                    activeDropdown === item.id
                                      ? "rotate(180deg)"
                                      : "rotate(0deg)",
                                }}
                              />
                            </button>

                            <AnimatePresence>
                              {activeDropdown === item.id && item.columns && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{
                                    duration: 0.3,
                                    ease: "easeInOut",
                                  }}
                                  className="uipm:overflow-hidden"
                                >
                                  <div className="uipm:py-4">
                                    {item.columns.map((column, columnIndex) => {
                                      const parentTitle = item.title;
                                      return (
                                        <div
                                          key={column.id}
                                          className="uipm:mb-6"
                                        >
                                          {column.title && (
                                            <h3 className="uipm:text-(length:--typography-sizes-body-large) uipm:font-bold uipm:text-[var(--typography-colors-text-accent)] uipm:my-4 uipm:leading-[var(--typography-line-heights-body-large)]">
                                              {column.title}
                                            </h3>
                                          )}
                                          <div className="uipm:space-y-3">
                                            {column.items.map((subItem) => {
                                              const isSubItemActive =
                                                activeSubItemId === subItem.id;
                                              return (
                                                <div key={subItem.id}>
                                                  {subItem.hasSubgroup ? (
                                                    <div>
                                                      <button
                                                        onClick={() =>
                                                          setActiveSubgroup(
                                                            activeSubgroup ===
                                                              subItem.id
                                                              ? null
                                                              : subItem.id,
                                                          )
                                                        }
                                                        className="uipm:flex uipm:items-center uipm:gap-3 uipm:py-2 uipm:w-full uipm:text-left uipm:cursor-pointer"
                                                      >
                                                        {subItem.icon && (
                                                          <div className="uipm:w-[30px] uipm:h-[30px] uipm:shrink-0">
                                                            <Image
                                                              src={
                                                                subItem.icon.src
                                                              }
                                                              alt={
                                                                subItem.icon.alt
                                                              }
                                                              width={30}
                                                              height={30}
                                                            />
                                                          </div>
                                                        )}
                                                        <span
                                                          className={cn(
                                                            "uipm:flex-1 uipm:text-(length:--typography-sizes-body-medium) uipm:font-bold uipm:leading-[var(--typography-line-heights-body-medium)]",
                                                            isSubItemActive
                                                              ? "uipm:text-[var(--color-color-accent)]"
                                                              : "uipm:text-[var(--typography-colors-text-faded-dark)]",
                                                          )}
                                                        >
                                                          {subItem.title}
                                                        </span>
                                                        <ChevronRightIcon
                                                          className="uipm:transition-transform uipm:self-center"
                                                          color="var(--typography-colors-text-accent)"
                                                          style={{
                                                            transform:
                                                              activeSubgroup ===
                                                              subItem.id
                                                                ? "rotate(90deg)"
                                                                : "rotate(0deg)",
                                                          }}
                                                        />
                                                      </button>

                                                      <AnimatePresence>
                                                        {activeSubgroup ===
                                                          subItem.id &&
                                                          subItem.subgroupItems && (
                                                            <motion.div
                                                              initial={{
                                                                height: 0,
                                                                opacity: 0,
                                                              }}
                                                              animate={{
                                                                height: "auto",
                                                                opacity: 1,
                                                              }}
                                                              exit={{
                                                                height: 0,
                                                                opacity: 0,
                                                              }}
                                                              transition={{
                                                                duration: 0.3,
                                                                ease: "easeInOut",
                                                              }}
                                                              className="uipm:overflow-hidden uipm:ml-9"
                                                            >
                                                              <div className="uipm:py-2 uipm:space-y-2">
                                                                {subItem.subgroupItems.map(
                                                                  (
                                                                    subgroupItem,
                                                                  ) => (
                                                                    <Link
                                                                      key={
                                                                        subgroupItem.id
                                                                      }
                                                                      href={
                                                                        subgroupItem.href
                                                                      }
                                                                      noFollow={
                                                                        subgroupItem.noFollow
                                                                      }
                                                                      className="uipm:block uipm:py-1 uipm:text-(length:--typography-sizes-body-small) uipm:font-medium uipm:text-[var(--typography-colors-text-faded-dark)] uipm:cursor-pointer"
                                                                      onClick={() => {
                                                                        setIsMobileMenuOpen(
                                                                          false,
                                                                        );
                                                                        onSubCategoryLinkClick?.(
                                                                          subgroupItem.title,
                                                                          subgroupItem.href,
                                                                          parentTitle,
                                                                        );
                                                                      }}
                                                                    >
                                                                      {
                                                                        subgroupItem.title
                                                                      }
                                                                    </Link>
                                                                  ),
                                                                )}
                                                              </div>
                                                            </motion.div>
                                                          )}
                                                      </AnimatePresence>
                                                    </div>
                                                  ) : (
                                                    <Link
                                                      href={subItem.href}
                                                      noFollow={
                                                        subItem.noFollow
                                                      }
                                                      className="uipm:flex uipm:items-center uipm:gap-3 uipm:py-2 uipm:cursor-pointer"
                                                      onClick={() => {
                                                        setIsMobileMenuOpen(
                                                          false,
                                                        );

                                                        if (column.title) {
                                                          onCategoryLinkClick?.(
                                                            subItem.title,
                                                            subItem.href,
                                                            column.title,
                                                          );
                                                        } else {
                                                          onSubCategoryLinkClick?.(
                                                            subItem.title,
                                                            subItem.href,
                                                            parentTitle,
                                                          );
                                                        }
                                                      }}
                                                    >
                                                      {subItem.icon && (
                                                        <div className="uipm:w-[30px] uipm:h-[30px] uipm:shrink-0">
                                                          <Image
                                                            src={
                                                              subItem.icon.src
                                                            }
                                                            alt={
                                                              subItem.icon.alt
                                                            }
                                                            width={30}
                                                            height={30}
                                                          />
                                                        </div>
                                                      )}
                                                      <span
                                                        className={cn(
                                                          "uipm:text-(length:--typography-sizes-body-medium) uipm:font-medium uipm:leading-[var(--typography-line-heights-body-medium)]",
                                                          isSubItemActive
                                                            ? "uipm:text-[var(--color-color-accent)]"
                                                            : "uipm:text-[var(--typography-colors-text-faded-dark)]",
                                                        )}
                                                      >
                                                        {subItem.title}
                                                      </span>
                                                    </Link>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </>
                        ) : (
                          <Link
                            href={item.href || "#"}
                            noFollow={item.noFollow}
                            className={cn(
                              "uipm:flex uipm:items-center uipm:justify-between uipm:w-full uipm:text-left uipm:text-(length:--typography-sizes-body-medium) uipm:font-medium uipm:leading-[var(--typography-line-heights-body-medium)] uipm:border-b uipm:border-[var(--color-color-light)] uipm:cursor-pointer uipm:h-[64px]",
                              isActive
                                ? "uipm:text-[var(--color-color-accent)]"
                                : "uipm:text-[var(--typography-colors-text-faded-dark)]",
                            )}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <span className="uipm:flex-1">{item.title}</span>
                          </Link>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div>
                  {/* <div className="uipm:my-[var(--margin-margin-default)]">
                    <button
                      onClick={onLanguageClick}
                      className="uipm:flex uipm:items-center uipm:gap-2 uipm:text-(length:--typography-sizes-body-medium) uipm:text-[var(--typography-colors-text-faded-dark)] uipm:py-2 uipm:cursor-pointer"
                    >
                      <GlobeIcon
                        className="uipm:shrink-0"
                        color="var(--typography-colors-text-accent)"
                      />
                      {language}
                    </button>
                  </div> */}

                  {signInItems && signInItems.length > 0 ? (
                    <div className="uipm:flex uipm:flex-col uipm:gap-3 uipm:w-full uipm:my-[var(--margin-margin-default)]">
                      {signInItems.map((item, index) => {
                        if (item.variant === "button") {
                          return (
                            <Button
                              key={index}
                              href={item.href}
                              noFollow={item.noFollow}
                              target={item.target}
                              navatticUrl={item.navatticUrl}
                              navatticTitle={item.navatticTitle}
                              navatticMobileUrl={item.navatticMobileUrl}
                              variant={item.buttonVariant || "primary"}
                              size={item.buttonSize || "medium"}
                              onClick={item.onClick}
                              className="uipm:w-full uipm:text-center"
                            >
                              {item.text}
                            </Button>
                          );
                        }

                        return (
                          <Button
                            key={index}
                            href={item.href}
                            noFollow={item.noFollow}
                            target={item.target}
                            onClick={item.onClick}
                            variant="tertiary"
                            className="uipm:w-full"
                          >
                            {item.text}
                          </Button>
                        );
                      })}
                    </div>
                  ) : signInHref ? (
                    <Button
                      href={signInHref}
                      noFollow={signInNoFollow}
                      variant="primary"
                      className="uipm:w-full"
                    >
                      {signInText}
                    </Button>
                  ) : (
                    <Button
                      onClick={onSignInClick}
                      variant="primary"
                      className="uipm:w-full"
                    >
                      {signInText}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
